import { useState } from "react";
import StartLearningButton from "@/components/learners/StartLearningButton";
import { useLearnerSeatCutover } from "@/hooks/useLearnerSeatCutover";
import { Lightbulb, X, ChevronRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import logoSvg from "@/assets/logo.svg";
import { BRAND } from "@/lib/brand";

interface Props {
  onClose: () => void;
}

const TIPS = [
  "Take HeirWise one lesson at a time — don't rush just to finish.",
  "Consistency matters more than speed. The goal is to understand and apply what you learn.",
  "If something doesn't click, dig deeper. Use AI guidance, adaptive support, and outside research.",
  "Going down rabbit holes is a good thing when it makes you more curious and more capable.",
  "One of the most valuable skills you can build is learning how to teach yourself.",
];

export default function PostOnboardingModal({ onClose }: Props) {
  const [view, setView] = useState<"guide" | "founder">("guide");
  const isGuide = view === "guide";
  const { enabled: seatCutover } = useLearnerSeatCutover();

  return (
    <div className="fixed inset-0 z-[200] overflow-y-auto bg-background/95 backdrop-blur-xl">
      <div className="pointer-events-none fixed inset-0 -z-0">
        <div className="absolute -top-32 -left-32 w-[36rem] h-[36rem] rounded-full bg-primary/15 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-[36rem] h-[36rem] rounded-full bg-secondary/15 blur-3xl" />
      </div>

      <div className="relative min-h-full flex items-center justify-center p-4 py-8">
        <div className="w-full max-w-lg space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src={logoSvg} alt="HeirWise" className="w-7 h-7" />
              <span className="font-heading font-bold text-sm text-foreground tracking-wide">
                {BRAND.productName}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              Close <X className="w-3 h-3 ml-1" />
            </Button>
          </div>

          <div className="relative rounded-3xl overflow-hidden animate-scale-in">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-secondary/20 opacity-60 blur-2xl -z-10" />
            <div className="glass-card border border-white/10 p-6 sm:p-8 space-y-5">
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => setView("guide")}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-heading font-semibold tracking-wide transition-all ${
                    isGuide
                      ? "bg-gradient-to-r from-primary to-secondary text-white shadow-glow"
                      : "bg-card/50 border border-border/60 text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Guide to Success
                </button>
                <button
                  onClick={() => setView("founder")}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-heading font-semibold tracking-wide transition-all ${
                    !isGuide
                      ? "bg-gradient-to-r from-primary to-secondary text-white shadow-glow"
                      : "bg-card/50 border border-border/60 text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Why HeirWise Exists
                </button>
              </div>

              <div className="flex flex-col items-center gap-2">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 border border-primary/25 flex items-center justify-center overflow-hidden shadow-glow">
                  {isGuide ? (
                    <Lightbulb className="w-7 h-7 text-primary" />
                  ) : (
                    <img src={logoSvg} alt="HeirWise" className="w-9 h-9 object-contain" />
                  )}
                </div>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-primary/10 border border-primary/20">
                  <Sparkles className="w-2.5 h-2.5 text-primary" />
                  <span className="text-[9px] uppercase tracking-[0.18em] text-primary font-semibold">
                    {isGuide ? "Mindset" : "Origin"}
                  </span>
                </div>
              </div>

              {isGuide ? (
                <div className="space-y-4">
                  <h2 className="font-heading font-bold text-2xl text-foreground text-center tracking-tight">
                    Guide to Success
                  </h2>
                  <ul className="space-y-2.5">
                  {TIPS.map((tip, i) => (
                    <li
                      key={i}
                      className="flex gap-3 text-sm text-muted-foreground leading-relaxed rounded-xl p-3 bg-card/40 border border-border/50"
                    >
                      <span className="shrink-0 w-6 h-6 rounded-lg bg-gradient-to-br from-primary to-secondary text-white text-[11px] font-bold flex items-center justify-center">
                        {i + 1}
                      </span>
                      <span className="text-foreground/85">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="space-y-4">
                <h2 className="font-heading font-bold text-2xl text-foreground text-center tracking-tight">
                  Why HeirWise Exists
                </h2>
                <div className="rounded-xl p-4 bg-gradient-to-br from-primary/10 via-card/40 to-secondary/10 border border-primary/15">
                  <p className="text-sm text-foreground/85 leading-relaxed">
                    HeirWise started with a simple thought: I wanted my daughter to have access to the
                    knowledge I had to fight to learn on my own. Growing up, I wasn't taught how money
                    really works, and these conversations simply didn't happen.
                  </p>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    When I found out I was going to be a father, it became clear: Economic & Enterprise Education
                    shouldn't be something people discover years too late. HeirWise exists to change
                    how young people see money, opportunity, and their future.
                  </p>
                  <p className="mt-4 text-xs text-muted-foreground/70 italic text-right">
                    — The HeirWise Founder
                  </p>
                </div>
              </div>
            )}

              <div className="pt-1 flex justify-center">
                {seatCutover ? (
                  <StartLearningButton onBeforeNavigate={onClose} />
                ) : (
                <Button
                  onClick={onClose}
                  className="bg-gradient-to-r from-primary to-secondary text-white shadow-glow hover:shadow-[0_0_24px_rgba(99,102,241,0.6)] hover:opacity-95 transition-all"
                >
                  Start Learning <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
